import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grape, HelpCircle, Check, AlertCircle, Clock, ArrowRight, ArrowLeft, Search, Filter, MoreHorizontal, ArrowDownLeft, ArrowUpRight, CheckCircle2, FileText, Database, GitMerge, ShieldAlert, Loader2, ChevronUp, ChevronDown, ArrowUpDown, X, CreditCard, Building2, Hash, Tag, Calendar, Columns, List, ArrowRightLeft } from 'lucide-react';
import { Button } from './ui/Button';
export function ReconcilePage() {
  const navigate = useNavigate();
  // Loading State
  const [isProcessing, setIsProcessing] = useState(true);
  const [progress, setProgress] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  // View Mode State
  const [viewMode, setViewMode] = useState<'table' | 'sideBySide'>('table');
  // Filter & Sort State
  const [statusFilter, setStatusFilter] = useState<'all' | 'matched' | 'anomaly' | 'pending'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'status' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Side Panel State
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
  // Inline Compare State
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  // Auto-Match Animation State
  const [isAutoMatching, setIsAutoMatching] = useState(false);
  const [autoMatchingId, setAutoMatchingId] = useState<number | null>(null);
  const [justMatchedIds, setJustMatchedIds] = useState<Set<number>>(new Set());
  // Dashboard Data State
  const [transactions, setTransactions] = useState([{
    id: 1,
    desc: 'Stripe Payout #8823',
    sub: 'Incoming Transfer',
    date: 'Oct 24, 2025',
    amount: '+$4,250.00',
    status: 'matched',
    type: 'incoming',
    category: 'Revenue',
    reference: 'TXN-2025-8823',
    account: 'Chase Business Checking',
    matchedWith: 'QuickBooks Invoice #1087',
    confidence: 99
  }, {
    id: 2,
    desc: 'Unknown Vendor',
    sub: 'Card ending 4242',
    date: 'Oct 23, 2025',
    amount: '-$129.99',
    status: 'anomaly',
    type: 'outgoing',
    category: 'Uncategorized',
    reference: 'TXN-2025-4401',
    account: 'Amex Platinum (...4242)',
    matchedWith: 'No match found',
    confidence: 12
  }, {
    id: 3,
    desc: 'AWS Web Services',
    sub: 'Infrastructure',
    date: 'Oct 22, 2025',
    amount: '-$842.50',
    status: 'pending',
    type: 'outgoing',
    category: 'Infrastructure',
    reference: 'TXN-2025-7720',
    account: 'Chase Business Checking',
    matchedWith: 'Pending review',
    confidence: 85
  }, {
    id: 4,
    desc: 'Client Invoice #1024',
    sub: 'Direct Deposit',
    date: 'Oct 21, 2025',
    amount: '+$1,500.00',
    status: 'matched',
    type: 'incoming',
    category: 'Revenue',
    reference: 'TXN-2025-1024',
    account: 'Chase Business Checking',
    matchedWith: 'QuickBooks Invoice #1024',
    confidence: 97
  }, {
    id: 5,
    desc: 'Slack Technologies',
    sub: 'Software Subscription',
    date: 'Oct 20, 2025',
    amount: '-$12.50',
    status: 'matched',
    type: 'outgoing',
    category: 'Software & SaaS',
    reference: 'TXN-2025-0512',
    account: 'Chase Business Checking',
    matchedWith: 'QuickBooks Expense #3391',
    confidence: 95
  }]);
  const totalTransactions = 287;
  const loadingSteps = [{
    text: 'Reading bank statements...',
    icon: FileText
  }, {
    text: 'Extracting 287 transactions...',
    icon: Database
  }, {
    text: 'Matching transactions across accounts...',
    icon: GitMerge
  }, {
    text: 'Detecting anomalies...',
    icon: ShieldAlert
  }, {
    text: 'Finalizing reconciliation results...',
    icon: CheckCircle2
  }];
  useEffect(() => {
    if (!isProcessing) return;
    const duration = 4000; // 4 seconds total
    const intervalTime = 50; // Update every 50ms
    const steps = duration / intervalTime;
    const increment = 100 / steps;
    let currentProgress = 0;
    const timer = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(timer);
        setTimeout(() => setIsProcessing(false), 800); // Small delay at 100% before showing dashboard
      }
      setProgress(Math.min(currentProgress, 100));
      setProcessedCount(Math.floor(currentProgress / 100 * totalTransactions));
      // Update step index based on progress thresholds
      const stepIndex = Math.min(Math.floor(currentProgress / 100 * loadingSteps.length), loadingSteps.length - 1);
      setCurrentStepIndex(stepIndex);
    }, intervalTime);
    return () => clearInterval(timer);
  }, [isProcessing]);
  const handleApprove = (id: number) => {
    setTransactions(transactions.map(t => t.id === id ? {
      ...t,
      status: 'approved'
    } : t));
  };
  const handleAutoMatchAll = () => {
    const unmatchedIds = transactions.filter(t => t.status === 'anomaly' || t.status === 'pending').map(t => t.id);
    if (unmatchedIds.length === 0 || isAutoMatching) return;
    setIsAutoMatching(true);
    unmatchedIds.forEach((id, index) => {
      // Phase 1: Highlight the row (processing animation)
      setTimeout(() => {
        setAutoMatchingId(id);
      }, index * 800);
      // Phase 2: Change status to matched with flash
      setTimeout(() => {
        setAutoMatchingId(null);
        setJustMatchedIds(prev => new Set(prev).add(id));
        setTransactions(prev => prev.map(t => t.id === id ? {
          ...t,
          status: 'matched',
          matchedWith: t.status === 'anomaly' ? 'Auto-matched by Berry AI' : t.matchedWith === 'Pending review' ? 'QuickBooks Entry (Auto-matched)' : t.matchedWith,
          confidence: t.status === 'anomaly' ? 78 : Math.min(t.confidence + 10, 99)
        } : t));
      }, index * 800 + 500);
      // Phase 3: Remove the flash highlight
      setTimeout(() => {
        setJustMatchedIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, index * 800 + 1500);
    });
    // All done
    setTimeout(() => {
      setIsAutoMatching(false);
      setAutoMatchingId(null);
    }, unmatchedIds.length * 800 + 1500);
  };
  const stats = {
    total: transactions.length,
    matched: transactions.filter(t => t.status === 'matched' || t.status === 'approved').length,
    anomalies: transactions.filter(t => t.status === 'anomaly').length,
    pending: transactions.filter(t => t.status === 'pending').length
  };
  const handleSort = (column: 'date' | 'amount' | 'status') => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };
  const parseAmount = (amount: string) => {
    return parseFloat(amount.replace(/[^0-9.-]/g, ''));
  };
  const parseDate = (dateStr: string) => {
    return new Date(dateStr).getTime();
  };
  const statusOrder: Record<string, number> = {
    anomaly: 0,
    pending: 1,
    matched: 2,
    approved: 3
  };
  const filteredAndSorted = transactions.filter(t => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'matched') return t.status === 'matched' || t.status === 'approved';
    return t.status === statusFilter;
  }).sort((a, b) => {
    if (!sortBy) return 0;
    let comparison = 0;
    if (sortBy === 'date') {
      comparison = parseDate(a.date) - parseDate(b.date);
    } else if (sortBy === 'amount') {
      comparison = parseAmount(a.amount) - parseAmount(b.amount);
    } else if (sortBy === 'status') {
      comparison = (statusOrder[a.status] ?? 0) - (statusOrder[b.status] ?? 0);
    }
    return sortDir === 'asc' ? comparison : -comparison;
  });
  const SortIcon = ({
    column
  }: {
    column: 'date' | 'amount' | 'status';
  }) => {
    if (sortBy !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" data-id="element-484" />;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3 ml-1 text-violet-600" data-id="element-485" /> : <ChevronDown className="h-3 w-3 ml-1 text-violet-600" data-id="element-486" />;
  };
  const CurrentStepIcon = loadingSteps[currentStepIndex].icon;
  const handleRowClick = (t: (typeof transactions)[0]) => {
    setSelectedTransaction(t);
  };
  const toggleCompare = (id: number) => {
    setExpandedRowId(expandedRowId === id ? null : id);
  };
  const closePanel = () => {
    setSelectedTransaction(null);
  };
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600 bg-green-100';
    if (confidence >= 50) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-487">
      {/* Simplified Top Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-488">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center" data-id="element-489">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/statements')} data-id="element-490">

            <div className="bg-violet-100 p-1.5 rounded-lg mr-2" data-id="element-491">
              <Grape className="h-5 w-5 text-violet-600" data-id="element-492" />
            </div>
            <span className="text-lg font-bold text-gray-900 tracking-tight" data-id="element-493">
              Berry Finance
            </span>
          </div>
          <button className="text-sm font-medium text-gray-500 hover:text-gray-900 flex items-center gap-1.5 transition-colors" data-id="element-494">
            <HelpCircle className="h-4 w-4" data-id="element-495" />
            Need help?
          </button>
        </div>
      </header>

      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8 flex flex-col" data-id="element-496">
        {isProcessing /* LOADING STATE */ ? <div className="flex-grow flex flex-col items-center justify-center max-w-2xl mx-auto w-full text-center" data-id="element-497">
            {/* Progress Stepper - Only during processing */}
            <div className="mb-12 max-w-3xl w-full" data-id="element-498">
              <div className="flex items-center justify-between relative" data-id="element-499">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-gray-200 -z-10" data-id="element-500"></div>
                <div className="flex flex-col items-center bg-gray-50 px-2" data-id="element-501">
                  <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold text-sm mb-2 ring-4 ring-gray-50" data-id="element-502">
                    <CheckCircle2 className="h-5 w-5" data-id="element-503" />
                  </div>
                  <span className="text-xs font-bold text-violet-600 uppercase tracking-wide" data-id="element-504">
                    Connect
                  </span>
                </div>
                <div className="flex flex-col items-center bg-gray-50 px-2" data-id="element-505">
                  <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold text-sm mb-2 ring-4 ring-gray-50" data-id="element-506">
                    <CheckCircle2 className="h-5 w-5" data-id="element-507" />
                  </div>
                  <span className="text-xs font-bold text-violet-600 uppercase tracking-wide" data-id="element-508">
                    Upload
                  </span>
                </div>
                <div className="flex flex-col items-center bg-gray-50 px-2" data-id="element-509">
                  <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold text-sm mb-2 ring-4 ring-gray-50" data-id="element-510">
                    3
                  </div>
                  <span className="text-xs font-bold text-violet-600 uppercase tracking-wide" data-id="element-511">
                    Reconcile
                  </span>
                </div>
              </div>
            </div>

            <div className="relative mb-8" data-id="element-512">
              <div className="absolute inset-0 bg-violet-200 rounded-full animate-ping opacity-20" data-id="element-513"></div>
              <div className="relative w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-lg border-4 border-violet-100" data-id="element-514">
                <Grape className="h-10 w-10 text-violet-600 animate-pulse" data-id="element-515" />
              </div>
              <div className="absolute inset-0 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" data-id="element-516"></div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-517">
              Processing your statements...
            </h1>
            <p className="text-gray-500 mb-10" data-id="element-518">
              Berry is analyzing your transactions and finding matches.
            </p>

            <div className="w-full max-w-md mb-4" data-id="element-519">
              <div className="flex justify-between text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide" data-id="element-520">
                <span data-id="element-521">Progress</span>
                <span data-id="element-522">{Math.round(progress)}%</span>
              </div>
              <div className="h-3 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-523">
                <div className="h-full bg-violet-600 rounded-full transition-all duration-100 ease-out" style={{
              width: `${progress}%`
            }} data-id="element-524">
              </div>
              </div>
            </div>

            <div className="flex flex-col items-center h-16" data-id="element-525">
              <div className="flex items-center gap-2 text-violet-700 font-medium animate-in fade-in slide-in-from-bottom-2 duration-300 key={currentStepIndex}" data-id="element-526">
                <CurrentStepIcon className="h-5 w-5" data-id="element-527" />
                <span data-id="element-528">{loadingSteps[currentStepIndex].text}</span>
              </div>
              <p className="text-sm text-gray-400 mt-2" data-id="element-529">
                <span className="font-mono font-bold text-violet-600" data-id="element-530">
                  {processedCount}
                </span>{' '}
                of {totalTransactions} transactions processed
              </p>
            </div>
          </div> /* DASHBOARD STATE */ : <div className="max-w-6xl mx-auto w-full animate-in fade-in duration-700" data-id="element-531">
            {/* Success Banner */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 flex items-center gap-3 text-green-800 shadow-sm" data-id="element-532">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" data-id="element-533" />
              <p className="font-medium" data-id="element-534">
                Reconciliation complete! <span className="font-bold" data-id="element-535">264</span>{' '}
                of <span className="font-bold" data-id="element-536">287</span> transactions matched
                automatically.
              </p>
            </div>

            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-537">
              <div data-id="element-538">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-539">
                  Reconciliation Dashboard
                </h1>
                <p className="text-gray-600" data-id="element-540">
                  Berry has analyzed your transactions. Review matches and
                  resolve anomalies.
                </p>
              </div>
              <div className="flex gap-3" data-id="element-541">
                {/* View Toggle */}
                <div className="flex bg-gray-100 p-1 rounded-lg border border-gray-200" data-id="element-542">
                  <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-violet-600' : 'text-gray-500 hover:text-gray-900'}`} title="Table View" data-id="element-543">

                    <List className="h-4 w-4" data-id="element-544" />
                  </button>
                  <button onClick={() => setViewMode('sideBySide')} className={`p-1.5 rounded-md transition-all ${viewMode === 'sideBySide' ? 'bg-white shadow-sm text-violet-600' : 'text-gray-500 hover:text-gray-900'}`} title="Side by Side View" data-id="element-545">

                    <Columns className="h-4 w-4" data-id="element-546" />
                  </button>
                </div>

                <Button variant="outline" size="sm" data-id="element-547">
                  <Filter className="h-4 w-4 mr-2" data-id="element-548" />
                  Filter
                </Button>
                <Button variant="primary" size="sm" onClick={handleAutoMatchAll} disabled={isAutoMatching || stats.anomalies === 0 && stats.pending === 0} data-id="element-549">

                  {isAutoMatching ? <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="element-550" />
                      Matching...
                    </> : 'Auto-Match All'}
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-551">
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-552">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-553">
                  Total Transactions
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-554">
                  {stats.total}
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-555">
                <p className="text-sm font-medium text-green-600 mb-1" data-id="element-556">
                  Matched
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-557">
                  {stats.matched}
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-558">
                <p className="text-sm font-medium text-red-600 mb-1" data-id="element-559">
                  Anomalies
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-560">
                  {stats.anomalies}
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-561">
                <p className="text-sm font-medium text-amber-600 mb-1" data-id="element-562">
                  Pending Review
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-563">
                  {stats.pending}
                </p>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-1 mb-4 bg-white rounded-lg border border-gray-200 p-1 shadow-sm w-fit" data-id="element-564">
              {[{
            key: 'all' as const,
            label: 'All',
            count: transactions.length
          }, {
            key: 'matched' as const,
            label: 'Matched',
            count: stats.matched
          }, {
            key: 'anomaly' as const,
            label: 'Anomalies',
            count: stats.anomalies
          }, {
            key: 'pending' as const,
            label: 'Pending',
            count: stats.pending
          }].map(tab => <button key={tab.key} onClick={() => setStatusFilter(tab.key)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${statusFilter === tab.key ? 'bg-violet-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-565">

                  {tab.label}
                  <span className={`ml-1.5 text-xs ${statusFilter === tab.key ? 'text-violet-200' : 'text-gray-400'}`} data-id="element-566">

                    {tab.count}
                  </span>
                </button>)}
            </div>

            {/* View Content */}
            {viewMode === 'table' /* TABLE VIEW */ ? <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-24" data-id="element-567">
                <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-gray-100 bg-gray-50/50 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-568">
                  <div className="col-span-4" data-id="element-569">Transaction</div>
                  <div className="col-span-2 flex items-center cursor-pointer hover:text-gray-900 transition-colors select-none" onClick={() => handleSort('date')} data-id="element-570">

                    Date <SortIcon column="date" data-id="element-571" />
                  </div>
                  <div className="col-span-2 text-right flex items-center justify-end cursor-pointer hover:text-gray-900 transition-colors select-none" onClick={() => handleSort('amount')} data-id="element-572">

                    Amount <SortIcon column="amount" data-id="element-573" />
                  </div>
                  <div className="col-span-3 flex items-center cursor-pointer hover:text-gray-900 transition-colors select-none" onClick={() => handleSort('status')} data-id="element-574">

                    Status <SortIcon column="status" data-id="element-575" />
                  </div>
                  <div className="col-span-1" data-id="element-576"></div>
                </div>

                <div className="divide-y divide-gray-100" data-id="element-577">
                  {filteredAndSorted.map(t => <div key={t.id} data-id="element-578">
                      <div onClick={() => handleRowClick(t)} className={`grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-gray-50 transition-all duration-500 cursor-pointer ${t.status === 'anomaly' ? 'bg-red-50/30' : ''} ${expandedRowId === t.id ? 'bg-violet-50/30' : ''} ${autoMatchingId === t.id ? 'bg-violet-100 scale-[1.01] shadow-md ring-2 ring-violet-300' : ''} ${justMatchedIds.has(t.id) ? 'bg-green-50 ring-2 ring-green-300' : ''}`} data-id="element-579">

                        {/* Transaction Info */}
                        <div className="col-span-4 flex items-center gap-3" data-id="element-580">
                          <div className={`p-2 rounded-full transition-colors duration-500 ${autoMatchingId === t.id ? 'bg-violet-200' : justMatchedIds.has(t.id) ? 'bg-green-200' : t.type === 'incoming' ? 'bg-green-100' : t.status === 'anomaly' ? 'bg-red-100' : 'bg-blue-100'}`} data-id="element-581">

                            {autoMatchingId === t.id ? <Loader2 className="h-4 w-4 text-violet-600 animate-spin" data-id="element-582" /> : justMatchedIds.has(t.id) ? <CheckCircle2 className="h-4 w-4 text-green-600" data-id="element-583" /> : t.type === 'incoming' ? <ArrowDownLeft className={`h-4 w-4 ${t.type === 'incoming' ? 'text-green-600' : ''}`} data-id="element-584" /> : <ArrowUpRight className={`h-4 w-4 ${t.status === 'anomaly' ? 'text-red-600' : 'text-blue-600'}`} data-id="element-585" />}
                          </div>
                          <div data-id="element-586">
                            <p className="font-medium text-gray-900" data-id="element-587">
                              {t.desc}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-588">{t.sub}</p>
                          </div>
                        </div>

                        {/* Date */}
                        <div className="col-span-2 text-sm text-gray-600" data-id="element-589">
                          {t.date}
                        </div>

                        {/* Amount */}
                        <div className={`col-span-2 text-right text-sm font-medium ${t.type === 'incoming' ? 'text-green-600' : 'text-gray-900'}`} data-id="element-590">

                          {t.amount}
                        </div>

                        {/* Status */}
                        <div className="col-span-2" data-id="element-591">
                          {autoMatchingId === t.id ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700 animate-pulse" data-id="element-592">
                              <Loader2 className="h-3 w-3 animate-spin" data-id="element-593" />{' '}
                              Matching...
                            </span> : justMatchedIds.has(t.id) ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-id="element-594">
                              <CheckCircle2 className="h-3 w-3" data-id="element-595" /> Just Matched!
                            </span> : <>
                              {t.status === 'matched' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-id="element-596">
                                  <Check className="h-3 w-3" data-id="element-597" /> Matched
                                </span>}
                              {t.status === 'approved' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-id="element-598">
                                  <CheckCircle2 className="h-3 w-3" data-id="element-599" /> Approved
                                </span>}
                              {t.status === 'anomaly' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" data-id="element-600">
                                  <AlertCircle className="h-3 w-3" data-id="element-601" /> Anomaly
                                </span>}
                              {t.status === 'pending' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700" data-id="element-602">
                                  <Clock className="h-3 w-3" data-id="element-603" /> Pending
                                </span>}
                            </>}
                        </div>

                        {/* Actions */}
                        <div className="col-span-2 flex items-center justify-end gap-1" data-id="element-604">
                          <button onClick={e => {
                    e.stopPropagation();
                    toggleCompare(t.id);
                  }} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${expandedRowId === t.id ? 'bg-violet-100 text-violet-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`} title="Compare side by side" data-id="element-605">

                            <Columns className="h-3.5 w-3.5" data-id="element-606" />
                            <span className="hidden lg:inline" data-id="element-607">Compare</span>
                          </button>
                          {t.status === 'matched' && <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={e => {
                    e.stopPropagation();
                    handleApprove(t.id);
                  }} data-id="element-608">

                              <Check className="h-4 w-4 text-gray-400 hover:text-green-600" data-id="element-609" />
                            </Button>}
                          {t.status !== 'matched' && <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={e => e.stopPropagation()} data-id="element-610">

                              <MoreHorizontal className="h-4 w-4 text-gray-400" data-id="element-611" />
                            </Button>}
                        </div>
                      </div>

                      {/* Inline Side-by-Side Comparison */}
                      {expandedRowId === t.id && <div className="px-6 pb-4 bg-violet-50/20 border-t border-violet-100" data-id="element-612">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4" data-id="element-613">
                            {/* Bank Side */}
                            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-614">
                              <div className="flex items-center gap-2 mb-3" data-id="element-615">
                                <Building2 className="h-4 w-4 text-blue-600" data-id="element-616" />
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-617">
                                  Bank Statement
                                </span>
                              </div>
                              <div className="flex justify-between items-start mb-2" data-id="element-618">
                                <span className="text-xs text-gray-500" data-id="element-619">
                                  {t.date}
                                </span>
                                <span className={`font-mono font-bold text-lg ${t.type === 'incoming' ? 'text-green-600' : 'text-gray-900'}`} data-id="element-620">

                                  {t.amount}
                                </span>
                              </div>
                              <h4 className="font-bold text-gray-900 mb-1" data-id="element-621">
                                {t.desc}
                              </h4>
                              <p className="text-sm text-gray-500 mb-2" data-id="element-622">
                                {t.sub}
                              </p>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600" data-id="element-623">
                                {t.account}
                              </span>
                            </div>

                            {/* Accounting Side */}
                            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-624">
                              <div className="flex items-center gap-2 mb-3" data-id="element-625">
                                <Database className="h-4 w-4 text-violet-600" data-id="element-626" />
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-627">
                                  Accounting Software
                                </span>
                              </div>
                              {t.status === 'anomaly' && t.matchedWith === 'No match found' ? <div className="flex flex-col items-center justify-center text-center py-4 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg" data-id="element-628">
                                  <Search className="h-6 w-6 mb-2 opacity-50" data-id="element-629" />
                                  <p className="text-sm font-medium" data-id="element-630">
                                    No matching entry found
                                  </p>
                                  <Button variant="outline" size="sm" className="mt-2" onClick={e => e.stopPropagation()} data-id="element-631">

                                    Find Match
                                  </Button>
                                </div> : <>
                                  <div className="flex justify-between items-start mb-2" data-id="element-632">
                                    <span className="text-xs text-gray-500" data-id="element-633">
                                      {t.date}
                                    </span>
                                    <span className={`font-mono font-bold text-lg ${t.type === 'incoming' ? 'text-green-600' : 'text-gray-900'}`} data-id="element-634">

                                      {t.amount}
                                    </span>
                                  </div>
                                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-635">
                                    {t.matchedWith}
                                  </h4>
                                  <p className="text-sm text-gray-500 mb-2" data-id="element-636">
                                    {t.category}
                                  </p>
                                  <div className="flex items-center justify-between" data-id="element-637">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${getConfidenceColor(t.confidence)}`} data-id="element-638">

                                      {t.confidence}% Confidence
                                    </span>
                                    {t.status === 'matched' && <Button size="sm" variant="ghost" className="h-7 text-green-600 hover:text-green-700 hover:bg-green-50" onClick={e => {
                          e.stopPropagation();
                          handleApprove(t.id);
                        }} data-id="element-639">

                                        Approve
                                      </Button>}
                                  </div>
                                </>}
                            </div>
                          </div>
                        </div>}
                    </div>)}
                  {filteredAndSorted.length === 0 && <div className="px-6 py-12 text-center text-gray-500" data-id="element-640">
                      No transactions match this filter.
                    </div>}
                </div>
              </div> /* SIDE BY SIDE VIEW */ : <div className="space-y-4 mb-24" data-id="element-641">
                {/* Header */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2 px-2" data-id="element-642">
                  <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2" data-id="element-643">
                    <Building2 className="h-4 w-4" data-id="element-644" /> Bank Statement
                  </div>
                  <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2" data-id="element-645">
                    <Database className="h-4 w-4" data-id="element-646" /> Accounting Software
                  </div>
                </div>

                {filteredAndSorted.map(t => <div key={t.id} onClick={() => handleRowClick(t)} className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden ${t.status === 'anomaly' ? 'border-red-200' : 'border-gray-200'}`} data-id="element-647">

                    <div className="grid grid-cols-1 md:grid-cols-2 relative" data-id="element-648">
                      {/* Center Match Indicator */}
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col items-center" data-id="element-649">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 shadow-sm ${t.status === 'matched' || t.status === 'approved' ? 'bg-green-100 border-green-200 text-green-600' : t.status === 'anomaly' ? 'bg-red-100 border-red-200 text-red-600' : 'bg-amber-100 border-amber-200 text-amber-600'}`} data-id="element-650">

                          {t.status === 'matched' || t.status === 'approved' ? <Check className="h-4 w-4" data-id="element-651" /> : t.status === 'anomaly' ? <AlertCircle className="h-4 w-4" data-id="element-652" /> : <Clock className="h-4 w-4" data-id="element-653" />}
                        </div>
                      </div>

                      {/* Left Side: Bank */}
                      <div className="p-6 border-b md:border-b-0 md:border-r border-gray-100 bg-gray-50/30" data-id="element-654">
                        <div className="flex justify-between items-start mb-2" data-id="element-655">
                          <span className="text-xs font-medium text-gray-500" data-id="element-656">
                            {t.date}
                          </span>
                          <span className={`font-mono font-medium ${t.type === 'incoming' ? 'text-green-600' : 'text-gray-900'}`} data-id="element-657">

                            {t.amount}
                          </span>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1" data-id="element-658">
                          {t.desc}
                        </h4>
                        <p className="text-sm text-gray-500 mb-3" data-id="element-659">{t.sub}</p>
                        <div className="flex items-center gap-2" data-id="element-660">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600" data-id="element-661">
                            {t.account}
                          </span>
                        </div>
                      </div>

                      {/* Right Side: Accounting */}
                      <div className="p-6" data-id="element-662">
                        {t.status === 'anomaly' && t.matchedWith === 'No match found' ? <div className="h-full flex flex-col items-center justify-center text-center py-2 text-gray-400 border-2 border-dashed border-gray-100 rounded-lg" data-id="element-663">
                            <Search className="h-8 w-8 mb-2 opacity-50" data-id="element-664" />
                            <p className="text-sm font-medium" data-id="element-665">
                              No matching transaction found
                            </p>
                            <Button variant="outline" size="sm" className="mt-3" data-id="element-666">

                              Find Match
                            </Button>
                          </div> : <>
                            <div className="flex justify-between items-start mb-2" data-id="element-667">
                              <span className="text-xs font-medium text-gray-500" data-id="element-668">
                                {t.date}
                              </span>
                              <span className={`font-mono font-medium ${t.type === 'incoming' ? 'text-green-600' : 'text-gray-900'}`} data-id="element-669">

                                {t.amount}
                              </span>
                            </div>
                            <h4 className="font-bold text-gray-900 mb-1" data-id="element-670">
                              {t.matchedWith}
                            </h4>
                            <p className="text-sm text-gray-500 mb-3" data-id="element-671">
                              {t.category}
                            </p>
                            <div className="flex items-center justify-between" data-id="element-672">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${getConfidenceColor(t.confidence)}`} data-id="element-673">

                                {t.confidence}% Confidence
                              </span>
                              {t.status === 'matched' && <Button size="sm" variant="ghost" className="h-7 text-green-600 hover:text-green-700 hover:bg-green-50" onClick={e => {
                      e.stopPropagation();
                      handleApprove(t.id);
                    }} data-id="element-674">

                                  Approve
                                </Button>}
                            </div>
                          </>}
                      </div>
                    </div>
                  </div>)}
                {filteredAndSorted.length === 0 && <div className="px-6 py-12 text-center text-gray-500 bg-white rounded-xl border border-gray-200" data-id="element-675">
                    No transactions match this filter.
                  </div>}
              </div>}
          </div>}
      </main>

      {/* Bottom Action Bar - Only show when not processing */}
      {!isProcessing && <div className="bg-white border-t border-gray-200 py-4 px-4 sm:px-6 lg:px-8 sticky bottom-0 z-10 animate-in slide-in-from-bottom-4 duration-500" data-id="element-676">
          <div className="max-w-5xl mx-auto flex justify-between items-center" data-id="element-677">
            <Button variant="ghost" onClick={() => navigate('/statements')} className="text-gray-500 hover:text-gray-900" data-id="element-678">

              Back
            </Button>
            <div className="flex items-center gap-4" data-id="element-679">
              <span className="text-sm text-gray-500 hidden sm:inline" data-id="element-680">
                All transactions reviewed?
              </span>
              <Button variant="primary" onClick={() => navigate('/matched-result')} className="min-w-[160px]" data-id="element-681">

                Complete Setup
                <ArrowRight className="ml-2 h-4 w-4" data-id="element-682" />
              </Button>
            </div>
          </div>
        </div>}

      {/* Transaction Detail Side Panel */}
      {selectedTransaction && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 z-40 transition-opacity" onClick={closePanel} data-id="element-683" />


          {/* Side Panel */}
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200 animate-in slide-in-from-right duration-300" data-id="element-684">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50/50" data-id="element-685">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-686">
                Transaction Details
              </h2>
              <button onClick={closePanel} className="p-2 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-900 transition-colors" data-id="element-687">

                <X className="h-5 w-5" data-id="element-688" />
              </button>
            </div>

            {/* Panel Content */}
            <div className="flex-grow overflow-y-auto px-6 py-6" data-id="element-689">
              {/* Transaction Header */}
              <div className="flex items-start gap-4 mb-6" data-id="element-690">
                <div className={`p-3 rounded-xl ${selectedTransaction.type === 'incoming' ? 'bg-green-100' : selectedTransaction.status === 'anomaly' ? 'bg-red-100' : 'bg-blue-100'}`} data-id="element-691">

                  {selectedTransaction.type === 'incoming' ? <ArrowDownLeft className="h-6 w-6 text-green-600" data-id="element-692" /> : <ArrowUpRight className={`h-6 w-6 ${selectedTransaction.status === 'anomaly' ? 'text-red-600' : 'text-blue-600'}`} data-id="element-693" />}
                </div>
                <div className="flex-1" data-id="element-694">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-695">
                    {selectedTransaction.desc}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-696">
                    {selectedTransaction.sub}
                  </p>
                </div>
              </div>

              {/* Amount */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-center" data-id="element-697">
                <p className="text-sm text-gray-500 mb-1" data-id="element-698">Amount</p>
                <p className={`text-3xl font-bold ${selectedTransaction.type === 'incoming' ? 'text-green-600' : 'text-gray-900'}`} data-id="element-699">

                  {selectedTransaction.amount}
                </p>
              </div>

              {/* Status & Confidence */}
              <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-700">
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-701">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2" data-id="element-702">
                    Status
                  </p>
                  {selectedTransaction.status === 'matched' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-id="element-703">
                      <Check className="h-3 w-3" data-id="element-704" /> Matched
                    </span>}
                  {selectedTransaction.status === 'approved' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-id="element-705">
                      <CheckCircle2 className="h-3 w-3" data-id="element-706" /> Approved
                    </span>}
                  {selectedTransaction.status === 'anomaly' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" data-id="element-707">
                      <AlertCircle className="h-3 w-3" data-id="element-708" /> Anomaly
                    </span>}
                  {selectedTransaction.status === 'pending' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700" data-id="element-709">
                      <Clock className="h-3 w-3" data-id="element-710" /> Pending
                    </span>}
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-711">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2" data-id="element-712">
                    Confidence
                  </p>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${getConfidenceColor(selectedTransaction.confidence)}`} data-id="element-713">

                    {selectedTransaction.confidence}%
                  </span>
                </div>
              </div>

              {/* Detail Fields */}
              <div className="space-y-4 mb-6" data-id="element-714">
                <div className="flex items-center gap-3 py-3 border-b border-gray-100" data-id="element-715">
                  <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-716" />
                  <div data-id="element-717">
                    <p className="text-xs text-gray-500" data-id="element-718">Date</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-719">
                      {selectedTransaction.date}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 py-3 border-b border-gray-100" data-id="element-720">
                  <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-721" />
                  <div data-id="element-722">
                    <p className="text-xs text-gray-500" data-id="element-723">Account</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-724">
                      {selectedTransaction.account}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 py-3 border-b border-gray-100" data-id="element-725">
                  <Tag className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-726" />
                  <div data-id="element-727">
                    <p className="text-xs text-gray-500" data-id="element-728">Category</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-729">
                      {selectedTransaction.category}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 py-3 border-b border-gray-100" data-id="element-730">
                  <Hash className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-731" />
                  <div data-id="element-732">
                    <p className="text-xs text-gray-500" data-id="element-733">Reference</p>
                    <p className="text-sm font-mono text-gray-900" data-id="element-734">
                      {selectedTransaction.reference}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 py-3 border-b border-gray-100" data-id="element-735">
                  <GitMerge className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-736" />
                  <div data-id="element-737">
                    <p className="text-xs text-gray-500" data-id="element-738">Matched With</p>
                    <p className={`text-sm font-medium ${selectedTransaction.status === 'anomaly' ? 'text-red-600' : 'text-gray-900'}`} data-id="element-739">

                      {selectedTransaction.matchedWith}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Panel Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/50 flex gap-3" data-id="element-740">
              {selectedTransaction.status === 'matched' && <Button variant="primary" fullWidth onClick={() => {
            handleApprove(selectedTransaction.id);
            setSelectedTransaction({
              ...selectedTransaction,
              status: 'approved'
            });
          }} data-id="element-741">

                  <Check className="h-4 w-4 mr-2" data-id="element-742" />
                  Approve Match
                </Button>}
              {selectedTransaction.status === 'approved' && <Button variant="secondary" fullWidth disabled data-id="element-743">
                  <CheckCircle2 className="h-4 w-4 mr-2" data-id="element-744" />
                  Already Approved
                </Button>}
              {selectedTransaction.status === 'anomaly' && <>
                  <Button variant="outline" fullWidth className="border-red-200 text-red-700 hover:bg-red-50" data-id="element-745">

                    <AlertCircle className="h-4 w-4 mr-2" data-id="element-746" />
                    Flag for Review
                  </Button>
                  <Button variant="ghost" fullWidth data-id="element-747">
                    Dismiss
                  </Button>
                </>}
              {selectedTransaction.status === 'pending' && <Button variant="primary" fullWidth data-id="element-748">
                  <Check className="h-4 w-4 mr-2" data-id="element-749" />
                  Manually Match
                </Button>}
            </div>
          </div>
        </>}
    </div>;
}